import { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, RefreshCw, AlertTriangle, Database, Link2Off, GitBranch,
  CheckCircle2, ChevronDown, ChevronUp, Loader2, Wrench, Boxes, Type, ArrowRight, Sparkles
} from 'lucide-react';
import { canUserEdit } from '@/utils/permissions';

export default function DataIntegrity() {
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({ entities: true, orphans: true, counters: true, stock: true, naming: true, phantoms: true, anomalies: true });
  const [stockReport, setStockReport] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [namingReport, setNamingReport] = useState(null);
  const [namingLoading, setNamingLoading] = useState(false);
  const [normalizeBusy, setNormalizeBusy] = useState(null); // canonical key currently being normalized
  const [targetForGroup, setTargetForGroup] = useState({}); // {canonical: chosen targetName}
  const [namingFilter, setNamingFilter] = useState('');
  const [phantomReport, setPhantomReport] = useState(null);
  const [phantomLoading, setPhantomLoading] = useState(false);
  const [anomalyReport, setAnomalyReport] = useState(null);
  const [anomalyLoading, setAnomalyLoading] = useState(false);

  const canEdit = canUserEdit(user, '/settings/data-integrity');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/data-integrity`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); fetchStockReport(); fetchNamingReport(); fetchPhantomReport(); }, []);

  const fetchNamingReport = async () => {
    setNamingLoading(true);
    try {
      const res = await axios.get(`${API}/stores/commodity-naming-health`);
      setNamingReport(res.data);
    } catch (e) {
      console.error('naming health failed', e);
    } finally {
      setNamingLoading(false);
    }
  };

  const fetchPhantomReport = async () => {
    setPhantomLoading(true);
    try {
      const res = await axios.get(`${API}/stores/phantom-issues`);
      setPhantomReport(res.data);
    } catch (e) {
      console.error('phantom issues failed', e);
    } finally {
      setPhantomLoading(false);
    }
  };

  const handlePhantomRelabel = async (group) => {
    const target = window.prompt(
      `Relabel ${group.issues.length} phantom issue row(s) of '${group.displayName}' to which existing commodity?\n\n` +
      `Suggested closest match: ${group.nearestMatch} (score ${group.nearestMatchScore})`,
      group.nearestMatch || ''
    );
    if (!target) return;
    try {
      const r = await axios.post(`${API}/stores/phantom-issues/relabel`, {
        fromName: group.displayName,
        toName: target,
      });
      alert(r.data?.message || 'Relabelled');
      fetchPhantomReport();
      fetchData();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Relabel failed');
    }
  };

  const handlePhantomDelete = async (group) => {
    const ok = window.confirm(
      `DELETE ${group.issues.length} phantom issue row(s) for '${group.displayName}' totalling ${group.totalQty}?\n\n` +
      `This action is audit-logged (recoverable via DB restore) but the rows will disappear from the system. ` +
      `Use this ONLY if these issuance records are bogus / duplicates / test entries.`
    );
    if (!ok) return;
    try {
      const r = await axios.post(`${API}/stores/phantom-issues/delete`, {
        normalizedKey: group.normalizedKey,
      });
      alert(r.data?.message || 'Deleted');
      fetchPhantomReport();
      fetchData();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handlePhantomMarkService = async (group) => {
    const unit = window.prompt(
      `MARK "${group.displayName}" as a Service / One-Off Job?\n\n` +
      `This stamps a synthetic receipt of ${group.totalQty} units so the books balance and the row exits the phantom warning. ` +
      `Use for service work that didn't follow the standard PO → GRN → issue flow.\n\n` +
      `Unit (default NOS):`,
      'NOS'
    );
    if (unit === null) return; // cancelled
    try {
      const r = await axios.post(`${API}/stores/phantom-issues/mark-service`, {
        itemName: group.displayName,
        unit: (unit || 'NOS').trim(),
      });
      alert(r.data?.message || 'Marked as service');
      fetchPhantomReport();
      fetchData();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Mark as service failed');
    }
  };

  const fetchAnomalyReport = async () => {
    setAnomalyLoading(true);
    try {
      const res = await axios.get(`${API}/stores/stock-anomalies`);
      setAnomalyReport(res.data);
    } catch (e) {
      console.error('stock anomalies failed', e);
    } finally {
      setAnomalyLoading(false);
    }
  };

  const handleNormalize = async (group) => {
    const target = (targetForGroup[group.canonical] || group.recommended || '').trim();
    if (!target) {
      alert('Pick a target name first.');
      return;
    }
    const otherVariants = group.variants.filter((v) => v.name !== target).map((v) => v.name);
    if (otherVariants.length === 0) {
      alert(`No other variants to rename — '${target}' is the only name in this group.`);
      return;
    }
    if (!window.confirm(
      `Rename ${otherVariants.length} variant(s) to '${target}'?\n\n` +
      `From:\n${otherVariants.map((n) => `  • '${n}'`).join('\n')}\n\nTo:\n  • '${target}'`
    )) return;
    setNormalizeBusy(group.canonical);
    try {
      const res = await axios.post(`${API}/stores/commodity-naming-health/normalize`, {
        targetName: target,
        variants: otherVariants,
      });
      const u = res.data?.updated || {};
      const total = Object.values(u).reduce((s, n) => s + (n || 0), 0);
      alert(`Done — ${total} record(s) renamed across ${Object.entries(u).filter(([, n]) => n > 0).length} collection(s).`);
      fetchNamingReport();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Normalize failed');
    } finally {
      setNormalizeBusy(null);
    }
  };

  const fetchStockReport = async () => {
    setStockLoading(true);
    try {
      const res = await axios.get(`${API}/stores/consumable-stock/reconcile`);
      setStockReport(res.data);
    } catch (e) {
      // Non-fatal — section will simply be hidden
      console.error('stock reconcile failed', e);
    } finally {
      setStockLoading(false);
    }
  };

  const handleDedupeStock = async () => {
    if (!window.confirm(
      'Dedupe Stock Lots: this will collapse duplicate consumable_stock_lots ' +
      '(same QA approval recorded twice OR identical legacy GRN+material+rate+qty) ' +
      'and rebuild stock_inventory.quantity for every affected material. ' +
      'Continue?'
    )) return;
    setDeduping(true);
    try {
      const res = await axios.post(`${API}/stores/consumable-stock/dedupe-lots`, {});
      const r = res.data || {};
      alert(
        `Dedupe complete\n\n` +
        `• Duplicate lots deleted: ${r.deletedLots || 0}\n` +
        `• QA-duplicate groups cleaned: ${r.qaGroupsCleaned || 0}\n` +
        `• Legacy-duplicate groups cleaned: ${r.legacyGroupsCleaned || 0}\n` +
        `• Materials recomputed: ${r.materialsRecomputed?.length || 0}`
      );
      fetchStockReport();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Dedupe failed');
    } finally {
      setDeduping(false);
    }
  };

  const handleResync = async () => {
    if (!window.confirm('Resync all out-of-sync counters to the current DB max? This is safe and prevents future duplicates.')) return;
    setResyncing(true);
    try {
      const res = await axios.post(`${API}/admin/data-integrity/resync-counters`, {});
      alert(res.data?.message || 'Done');
      fetchData();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Resync failed');
    } finally {
      setResyncing(false);
    }
  };

  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  const allGreen = data && data.summary.duplicateRefs === 0 &&
                   data.summary.orphanRecords === 0 &&
                   data.summary.counterDrift === 0;

  if (loading && !data) {
    return (
      <div className="p-10 text-center text-gray-500" data-testid="data-integrity-loading">
        <Loader2 className="mx-auto mb-3 animate-spin text-gray-400" size={36} />
        Scanning all modules...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 text-red-700 rounded-lg" data-testid="data-integrity-error">
        <AlertTriangle className="inline mr-2" size={18} />
        {error}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="data-integrity-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="text-emerald-600" size={26} />
            Data Integrity
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Live scan for duplicates, orphaned records, and counter drift across all modules.
          </p>
          {data?.scannedAt && (
            <p className="text-xs text-gray-400 mt-0.5">
              Last scan: {new Date(data.scannedAt).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {canEdit && data?.summary.counterDrift > 0 && (
            <button
              onClick={handleResync}
              disabled={resyncing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60 text-sm"
              data-testid="resync-counters-btn"
            >
              <Wrench size={16} />
              {resyncing ? 'Resyncing...' : 'Resync Counters'}
            </button>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 text-sm"
            data-testid="refresh-scan-btn"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh Scan
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="data-integrity-summary">
        <SummaryCard
          icon={Database}
          label="Total Records"
          value={data.summary.totalRecords}
          tone="neutral"
        />
        <SummaryCard
          icon={AlertTriangle}
          label="Duplicate Refs"
          value={data.summary.duplicateRefs}
          tone={data.summary.duplicateRefs > 0 ? 'danger' : 'success'}
        />
        <SummaryCard
          icon={Link2Off}
          label="Orphan Records"
          value={data.summary.orphanRecords}
          tone={data.summary.orphanRecords > 0 ? 'danger' : 'success'}
        />
        <SummaryCard
          icon={GitBranch}
          label="Counter Drift"
          value={data.summary.counterDrift}
          tone={data.summary.counterDrift > 0 ? 'warning' : 'success'}
        />
        <SummaryCard
          icon={CheckCircle2}
          label="Status"
          value={allGreen ? 'Healthy' : 'Needs attention'}
          tone={allGreen ? 'success' : 'warning'}
        />
      </div>

      {/* Entities */}
      <Section
        title="Reference Number Audit"
        subtitle="Per-entity record counts and any duplicate reference numbers"
        expanded={expanded.entities}
        onToggle={() => toggle('entities')}
      >
        <div className="divide-y divide-gray-100">
          {data.entities.map((e) => (
            <div key={e.collection} className="py-3 flex items-start gap-4 px-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{e.label}</span>
                  <span className="text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                    {e.category}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5 font-mono">
                  {e.collection} · <span className="text-gray-400">{e.refField}</span>
                </div>
                {e.duplicates.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {e.duplicates.map((d) => (
                      <span
                        key={d.value}
                        className="inline-flex items-center gap-1 text-[11px] bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 rounded font-mono"
                      >
                        {d.value} <span className="opacity-60">×{d.count}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-gray-900">{e.totalRecords}</div>
                <div className={`text-xs font-medium mt-0.5 ${
                  e.duplicateCount > 0 ? 'text-rose-600' : 'text-emerald-600'
                }`}>
                  {e.duplicateCount > 0 ? `${e.duplicateCount} dup${e.duplicateCount > 1 ? 's' : ''}` : 'unique ✓'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Orphans */}
      <Section
        title="Orphaned Records"
        subtitle="Records referencing a parent that no longer exists"
        expanded={expanded.orphans}
        onToggle={() => toggle('orphans')}
        emptyMessage={data.orphans.length === 0 ? 'No orphaned records found. 🎉' : null}
      >
        <div className="divide-y divide-gray-100">
          {data.orphans.map((o, idx) => (
            <div key={idx} className="py-3 px-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{o.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5 font-mono">
                    {o.collection} · missing {o.refField}
                  </div>
                </div>
                <span className="text-sm font-bold text-rose-600">{o.count}</span>
              </div>
              {o.items?.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto border border-gray-100 rounded bg-gray-50">
                  <table className="w-full text-xs">
                    <tbody>
                      {o.items.map((it, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0">
                          <td className="px-3 py-1.5 font-mono text-gray-700">{it.id || '-'}</td>
                          <td className="px-3 py-1.5 text-gray-500">
                            → {o.refField}: <span className="font-mono">{it[o.refField] || it.poNumber || it.grnNo || '(empty)'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* Counter Drift */}
      <Section
        title="Counter Drift"
        subtitle="Counters that lag behind the actual DB max (can cause future collisions)"
        expanded={expanded.counters}
        onToggle={() => toggle('counters')}
        emptyMessage={data.counterIssues.length === 0 ? 'All counters in sync. 🎉' : null}
      >
        <div className="divide-y divide-gray-100">
          {data.counterIssues.map((c) => (
            <div key={c.counterId} className="py-3 px-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 font-mono">{c.counterId}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Prefix: <span className="font-mono">{c.prefix}</span> · Collection: <span className="font-mono">{c.collection}</span>
                </div>
              </div>
              <div className="text-right text-xs">
                <div>Counter: <span className="font-bold text-gray-700">{c.counter}</span></div>
                <div>DB Max: <span className="font-bold text-rose-600">{c.dbMax}</span></div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Consumable Stock — compact summary that delegates to Stock Health */}
      <Section
        title="Consumable Stock"
        subtitle="One place to fix material drift, duplicate lots, and orphan records — opens in the dedicated Stock Health page (used by everyone, with audit-logged Fix + Undo)."
        expanded={expanded.stock}
        onToggle={() => toggle('stock')}
      >
        <div className="p-5 space-y-4">
          {stockReport ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="text-[11px] uppercase text-gray-500">Total Materials</div>
                <div className="text-xl font-bold text-gray-900 mt-1">{stockReport.summary.totalMaterials}</div>
              </div>
              <div className={`bg-white border rounded-lg p-3 ${stockReport.summary.needsCleanup > 0 ? 'border-rose-300' : 'border-emerald-300'}`}>
                <div className="text-[11px] uppercase text-gray-500">Items With Drift</div>
                <div className={`text-xl font-bold mt-1 ${stockReport.summary.needsCleanup > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {stockReport.summary.needsCleanup}
                </div>
              </div>
              <div className={`bg-white border rounded-lg p-3 ${stockReport.summary.totalDuplicateQaLots > 0 ? 'border-rose-300' : 'border-gray-200'}`}>
                <div className="text-[11px] uppercase text-gray-500">Duplicate QA Lots</div>
                <div className={`text-xl font-bold mt-1 ${stockReport.summary.totalDuplicateQaLots > 0 ? 'text-rose-600' : 'text-gray-900'}`}>
                  {stockReport.summary.totalDuplicateQaLots}
                </div>
              </div>
              <div className={`bg-white border rounded-lg p-3 ${stockReport.summary.totalSuspectedLegacyDuplicates > 0 ? 'border-amber-300' : 'border-gray-200'}`}>
                <div className="text-[11px] uppercase text-gray-500">Legacy Duplicates</div>
                <div className={`text-xl font-bold mt-1 ${stockReport.summary.totalSuspectedLegacyDuplicates > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                  {stockReport.summary.totalSuspectedLegacyDuplicates}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">{stockLoading ? 'Scanning…' : '—'}</div>
          )}
          <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <strong>Why one page only?</strong> The detailed audit (per-row drift, "Fix this", "Undo last reconcile",
            orphan lots, orphan inventory, orphan material-issues) all live on{' '}
            <strong>Stock Health</strong>. Same equation as the Consumable Stock list page — they can never disagree.
            Click below to open it.
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => navigate('/stores/consumable-stock/health')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm shadow"
              data-testid="data-integrity-open-stock-health-btn"
            >
              <ShieldCheck size={16} /> Open Stock Health (Material)
              <ArrowRight size={14} />
            </button>
            <button
              onClick={() => navigate('/stores/service-consumption/health')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 text-sm"
              data-testid="data-integrity-open-service-stock-health-btn"
            >
              Service Stock Health <ArrowRight size={14} />
            </button>
            <button
              onClick={fetchStockReport}
              disabled={stockLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-60"
              data-testid="stock-reconcile-refresh"
            >
              <RefreshCw size={14} className={stockLoading ? 'animate-spin' : ''} /> Rescan
            </button>
            {canEdit && stockReport && (stockReport.summary.totalDuplicateQaLots + stockReport.summary.totalSuspectedLegacyDuplicates) > 0 && (
              <button
                onClick={handleDedupeStock}
                disabled={deduping}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700 disabled:opacity-60"
                data-testid="dedupe-stock-btn"
              >
                <Boxes size={14} />
                {deduping ? 'Deduping…' : 'Dedupe Duplicate Lots'}
              </button>
            )}
          </div>
        </div>
      </Section>

      {/* Commodity Naming Health */}
      <Section
        title="Commodity Naming Health"
        subtitle="Groups of commodity names that differ only by whitespace, case or punctuation — pick the canonical form to rename them in one click"
        expanded={expanded.naming}
        onToggle={() => toggle('naming')}
      >
        <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/50">
          <div className="text-xs text-gray-600">
            {namingReport ? (
              <>
                <span className={`font-semibold ${namingReport.summary.groupsWithVariants ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {namingReport.summary.groupsWithVariants}
                </span>{' '}
                group(s) with naming variants ·{' '}
                <span className="font-semibold">{namingReport.summary.totalRecordsAffected}</span> records affected
              </>
            ) : namingLoading ? 'Scanning…' : '—'}
          </div>
          <div className="flex items-center gap-2">
            {namingReport && namingReport.summary.groupsWithVariants > 0 && (
              <button
                onClick={async () => {
                  const ok = window.confirm(
                    `Normalize ALL ${namingReport.summary.groupsWithVariants} groups in one click?\n\n` +
                    `For each group, the most-used spelling will be kept as canonical, and every ` +
                    `other variant will be rewritten to match. This touches all 8 collections ` +
                    `(POs, GRNs, lots, inventory, master, material issues) and is fully audit-logged.\n\n` +
                    `Touches ~${namingReport.summary.totalRecordsAffected} records. Proceed?`
                  );
                  if (!ok) return;
                  try {
                    setNamingLoading(true);
                    const r = await axios.post(`${API}/stores/commodity-naming-health/normalize-all`);
                    alert(r.data?.message || 'Done');
                    await fetchNamingReport();
                  } catch (e) {
                    alert(e?.response?.data?.detail || 'Bulk normalize failed');
                  } finally {
                    setNamingLoading(false);
                  }
                }}
                disabled={namingLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-60 font-medium"
                data-testid="naming-normalize-all"
                title="Auto-pick the most-used spelling for every group and merge all variants in one shot"
              >
                <Sparkles size={12} /> Normalize ALL ({namingReport.summary.groupsWithVariants})
              </button>
            )}
            <button
              onClick={fetchNamingReport}
              disabled={namingLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-100 disabled:opacity-60"
              data-testid="naming-health-rescan"
            >
              <RefreshCw size={12} className={namingLoading ? 'animate-spin' : ''} /> Rescan
            </button>
          </div>
        </div>

        {namingReport && namingReport.data.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-100 bg-white">
            <input
              type="text"
              value={namingFilter}
              onChange={(e) => setNamingFilter(e.target.value)}
              placeholder="Filter groups by name fragment…"
              className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              data-testid="naming-filter-input"
            />
          </div>
        )}

        {namingReport && namingReport.data.length === 0 && !namingLoading && (
          <div className="py-6 text-center text-sm text-emerald-700 bg-emerald-50">
            <CheckCircle2 size={16} className="inline mr-1" /> No naming inconsistencies found.
          </div>
        )}

        {namingReport && namingReport.data.length > 0 && (
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {(() => {
              const q = namingFilter.trim().toUpperCase();
              const filtered = q
                ? namingReport.data.filter(
                    (g) => g.canonical.includes(q) || g.variants.some((v) => (v.name || '').toUpperCase().includes(q))
                  )
                : namingReport.data;
              const cap = q ? 200 : 50;
              return (
                <>
                  {filtered.slice(0, cap).map((group) => {
              const target = targetForGroup[group.canonical] || group.recommended;
              const isBusy = normalizeBusy === group.canonical;
              return (
                <div
                  key={group.canonical}
                  className="p-3 hover:bg-gray-50/60"
                  data-testid={`naming-group-${group.canonical}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Type size={14} className="text-gray-500 flex-shrink-0" />
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 truncate" title={group.canonical}>
                        {group.canonical}
                      </code>
                      <span className="text-[10px] text-gray-500">
                        {group.variants.length} variants · {group.totalRecords} record(s)
                      </span>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-2">
                        <select
                          value={target}
                          onChange={(e) => setTargetForGroup((p) => ({ ...p, [group.canonical]: e.target.value }))}
                          className="text-xs border border-gray-300 rounded px-2 py-1 bg-white max-w-[260px]"
                          data-testid={`naming-target-${group.canonical}`}
                        >
                          {group.variants.map((v) => (
                            <option key={v.name} value={v.name}>
                              "{v.name}" ({v.totalCount})
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleNormalize(group)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-60"
                          data-testid={`naming-normalize-${group.canonical}`}
                        >
                          <Wrench size={11} />
                          {isBusy ? 'Renaming…' : 'Normalize'}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-xs pl-5">
                    {group.variants.map((v) => (
                      <div key={v.name} className={`flex items-start justify-between gap-2 px-2 py-1 rounded ${v.name === target ? 'bg-indigo-50 border border-indigo-200' : 'bg-white border border-gray-100'}`}>
                        <span className="font-mono text-gray-800 truncate flex-1" title={v.name}>"{v.name}"</span>
                        <div className="flex flex-wrap gap-1 flex-shrink-0">
                          {Object.entries(v.locations).map(([coll, n]) => (
                            <span key={coll} className="text-[10px] bg-gray-100 text-gray-600 px-1 py-0.5 rounded">
                              {coll.replace(/_/g, ' ')} ×{n}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {namingReport.data.length > 50 && (
              <div className="px-3 py-2 text-xs text-gray-500 italic">
                Showing first {cap} of {filtered.length}{q ? ` matching` : ''} group(s){q ? '' : ` (of ${namingReport.data.length} total — use filter to find specific names)`}
              </div>
            )}
                </>
              );
            })()}
          </div>
        )}
      </Section>

      {/* ============ PHANTOM ISSUES ============ */}
      <Section
        title="Phantom Issuance Records"
        subtitle="material_issues rows whose commodity name has NO matching receipt (no PO / GRN / lot / stock). Either relabel them to a real commodity OR delete if they're bogus."
        expanded={expanded.phantoms}
        onToggle={() => toggle('phantoms')}
      >
        <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/50">
          <div className="text-xs text-gray-600">
            {phantomReport ? (
              <>
                <span className={`font-semibold ${phantomReport.summary.phantomGroups ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {phantomReport.summary.phantomGroups}
                </span>{' '}
                phantom group(s) ·{' '}
                <span className="font-semibold">{phantomReport.summary.phantomIssueRows}</span> issue row(s) ·{' '}
                <span className="font-semibold">{phantomReport.summary.totalQuantity}</span> total qty
              </>
            ) : phantomLoading ? 'Scanning…' : '—'}
          </div>
          <button
            onClick={fetchPhantomReport}
            disabled={phantomLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-100 disabled:opacity-60"
            data-testid="phantom-issues-rescan"
          >
            <RefreshCw size={12} className={phantomLoading ? 'animate-spin' : ''} /> Rescan
          </button>
        </div>

        {phantomReport && phantomReport.data.length === 0 && !phantomLoading && (
          <div className="py-6 text-center text-sm text-emerald-700 bg-emerald-50">
            <CheckCircle2 size={16} className="inline mr-1" /> No phantom issuance records found.
          </div>
        )}

        {phantomReport && phantomReport.data.length > 0 && (
          <div className="divide-y divide-gray-100">
            {phantomReport.data.map((g, idx) => (
              <div key={g.normalizedKey || idx} className="px-4 py-3" data-testid={`phantom-group-${idx}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm font-semibold text-rose-700 break-all">
                      {g.displayName}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      <span className="font-semibold">{g.issues.length}</span> issue row(s) · total{' '}
                      <span className="font-semibold">{g.totalQty}</span>
                      {g.nearestMatch && (
                        <span className="ml-2 text-gray-500">
                          · nearest existing match:{' '}
                          <span className="font-mono text-indigo-700">{g.nearestMatch}</span>{' '}
                          <span className="text-gray-400">({(g.nearestMatchScore * 100).toFixed(0)}%)</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePhantomRelabel(g)}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 font-medium"
                      data-testid={`phantom-relabel-${idx}`}
                      title="Rename all these issue rows to point to an existing commodity"
                    >
                      Relabel →
                    </button>
                    <button
                      onClick={() => handlePhantomMarkService(g)}
                      className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700 font-medium"
                      data-testid={`phantom-mark-service-${idx}`}
                      title="Treat as service work — stamps a synthetic receipt so books balance"
                    >
                      Mark as Service ✓
                    </button>
                    <button
                      onClick={() => handlePhantomDelete(g)}
                      className="px-3 py-1.5 bg-rose-600 text-white rounded text-xs hover:bg-rose-700 font-medium"
                      data-testid={`phantom-delete-${idx}`}
                      title="Delete all these issue rows (audit-logged, use only for bogus entries)"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                    Show {g.issues.length} issue record(s)
                  </summary>
                  <div className="mt-2 pl-3 border-l-2 border-gray-200 space-y-1">
                    {g.issues.map((iss, j) => (
                      <div key={iss.id || j} className="text-gray-600">
                        • <span className="font-mono">{iss.id}</span> — qty{' '}
                        <span className="font-semibold">{iss.quantity}</span> on{' '}
                        {iss.issueDate} to <span className="italic">{iss.issuedTo || '-'}</span>
                        {iss.department ? ` (${iss.department})` : ''}
                        {iss.purpose ? ` — ${iss.purpose}` : ''}
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ============ STOCK ANOMALIES (over-issued) ============ */}
      <Section
        title="Stock Anomalies — Over-Issued Items"
        subtitle="Commodities where total CONSUMED exceeds total RECEIVED (impossible without data corruption). Each anomaly lists every contributing issue record."
        expanded={expanded.anomalies}
        onToggle={() => toggle('anomalies')}
      >
        <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/50">
          <div className="text-xs text-gray-600">
            {anomalyReport ? (
              <>
                <span className={`font-semibold ${anomalyReport.summary.anomalyCount ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {anomalyReport.summary.anomalyCount}
                </span>{' '}
                anomaly group(s) ·{' '}
                <span className="font-semibold">{anomalyReport.summary.totalExcess}</span> total excess qty
              </>
            ) : anomalyLoading ? 'Scanning…' : '—'}
          </div>
          <button
            onClick={fetchAnomalyReport}
            disabled={anomalyLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-100 disabled:opacity-60"
            data-testid="anomalies-rescan"
          >
            <RefreshCw size={12} className={anomalyLoading ? 'animate-spin' : ''} /> Rescan
          </button>
        </div>

        {anomalyReport && anomalyReport.data.length === 0 && !anomalyLoading && (
          <div className="py-6 text-center text-sm text-emerald-700 bg-emerald-50">
            <CheckCircle2 size={16} className="inline mr-1" /> No over-issued anomalies detected.
          </div>
        )}

        {anomalyReport && anomalyReport.data.length > 0 && (
          <div className="divide-y divide-gray-100">
            {anomalyReport.data.map((a, idx) => (
              <div key={a.normalizedKey || idx} className="px-4 py-3" data-testid={`anomaly-row-${idx}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm font-semibold text-rose-700 break-all">
                      {a.displayName}
                    </div>
                    <div className="text-xs text-gray-700 mt-1 grid grid-cols-3 gap-4">
                      <div>Received: <span className="font-semibold">{a.totalReceived}</span></div>
                      <div>Consumed: <span className="font-semibold text-rose-600">{a.totalConsumed}</span></div>
                      <div>Excess: <span className="font-semibold text-rose-700">+{a.excess}</span></div>
                    </div>
                    {a.variants.length > 1 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Variants: {a.variants.map((v, i) => (
                          <span key={i} className="font-mono mr-1">"{v}"</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <a
                    href={`/api/stores/diagnose-commodity?name=${encodeURIComponent(a.displayName)}&fuzzy=true`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 font-medium"
                  >
                    Diagnose →
                  </a>
                </div>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                    Show {a.issueCount} contributing issue(s)
                  </summary>
                  <div className="mt-2 pl-3 border-l-2 border-gray-200 space-y-1 max-h-72 overflow-y-auto">
                    {a.issues.map((iss, j) => (
                      <div key={iss.id || j} className="text-gray-600">
                        • <span className="font-mono">{iss.id}</span> — qty{' '}
                        <span className="font-semibold">{iss.quantity}</span> on{' '}
                        {iss.issueDate || '?'} to <span className="italic">{iss.issuedTo || '-'}</span>
                        {iss.department ? ` (${iss.department})` : ''}
                        {iss.materialName !== a.displayName && (
                          <span className="ml-1 text-amber-700">[variant: "{iss.materialName}"]</span>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tone }) {
  const tones = {
    neutral: 'bg-gray-50 text-gray-700 border-gray-200',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    danger: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <div className={`px-4 py-3 rounded-lg border ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-xs font-medium opacity-80">
        <Icon size={14} />
        {label}
      </div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function Section({ title, subtitle, expanded, onToggle, children, emptyMessage }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
        </div>
        {expanded ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
      </button>
      {expanded && (
        <div>
          {emptyMessage ? (
            <div className="py-6 text-center text-sm text-gray-500">{emptyMessage}</div>
          ) : children}
        </div>
      )}
    </div>
  );
}
